/**
 * Domains: the client side of the portal's Domains API (search across registrars, cart, checkout,
 * the Domain Control Centre) plus the shared hooks and wording the domain screens use.
 *
 * Money is in pence (GBP), net of VAT unless the field says total.
 */
import { useQuery } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";

import { authedPortal } from "./api";
import { config } from "./config";
import { civilDay, formatDayMonthYear } from "./format";
import { ApiError, request } from "./http";
import type { LinkScheme } from "./types";

// ── types ───────────────────────────────────────────────────────────────────
export type DomainStatus =
  | "registering"
  | "transferring_in"
  | "active"
  | "expired"
  | "restoring"
  | "unrecoverable"
  | "released"
  | "transferring_out"
  | "transferred_out"
  | "failed";

/** The single status pill: problems first. */
export type DomainHealth = DomainStatus | "renewal_failed" | "expiring_soon";

export type Capability =
  | "suggest"
  | "register"
  | "renew"
  | "restore"
  | "transfer_in"
  | "transfer_check"
  | "transfer_out_control"
  | "auth_code"
  | "send_auth_code"
  | "transfer_lock"
  | "nameservers"
  | "dns_hosting"
  | "dnssec"
  | "privacy"
  | "contact_update"
  | "resend_verification";

export interface SearchResult {
  fqdn: string;
  display_name: string;
  status: "available" | "premium" | "taken" | "unsupported" | "unknown" | "yours";
  register_cents: number | null;
  renew_cents: number | null;
  transfer_cents: number | null;
  exact: boolean;
  suggested: boolean;
  in_cart: boolean;
  min_years: number;
  max_years: number;
  /** Set for names the workspace already owns. */
  domain_id: string | null;
  /** A restricted ending: shown in search, but bought through the CortexGrip team ("contact us"). */
  restricted: boolean;
  restriction_message: string | null;
  /** The person's open request for this name ("DR-…"), if they already asked. */
  request_number: string | null;
  /** Registration periods the registry allows, in years (empty: 1–10). */
  periods: number[];
  /** Shown, but can't be bought online right now (see BLOCKED_REASONS), and why in plain words. */
  blocked_reason: BlockedReason | null;
  blocked_message: string | null;
  /**
   * A registration promotion: `register_cents` is the offer on the first year and this the usual
   * first-year price. Renewals are always `renew_cents`.
   */
  standard_register_cents: number | null;
  promotion_label: string | null;
  promotion_ends_at: string | null;
}

/** Why a name is shown but can't be bought online right now. */
export type BlockedReason =
  | "PRICING_STALE"
  | "PRICE_UNDER_REVIEW"
  | "PREMIUM_QUOTE_REQUIRED"
  | "PREMIUM_NOT_SOLD"
  | "LOCAL_PRESENCE_REQUIRED"
  | "NO_PRICE"
  | (string & {});

/** How VAT applies to a domain ending. */
export type TaxTreatment = "standard" | "zero_rated" | "exempt" | "outside_scope";

/** An extra value a registry asks for (e.g. the language of a .eu registration). */
export interface TldField {
  key: string;
  label: string;
  /** "select" (one of `options`), "country" (ISO code), "date", "file" (the value is an uploaded file's id), "text"… */
  kind: "select" | "country" | "date" | "file" | "text" | (string & {});
  required: boolean;
  options: { value: string; label: string }[];
  description: string | null;
  /** Fields staff added: a format rule (regular expression) and the message shown when a value doesn't match. */
  pattern?: string | null;
  pattern_message?: string | null;
}

/** What a registry allows for an ending (GET /v1/domains/tld-rules). */
export interface TldRules {
  tld: string;
  sold: boolean;
  register_periods: number[];
  renew_periods: number[];
  transfer_supported: boolean | null;
  local_presence: "none" | "required" | "offered" | (string & {});
  fields: TldField[];
  tax: TaxTreatment;
  contact_roles: OtherRole[];
}

export type DomainRequestStatus = "new" | "contacted" | "fulfilled" | "declined" | "cancelled";

/** "Contact us to buy": a request to the CortexGrip team for a restricted domain. */
export interface DomainRequest {
  id: string;
  number: string;
  fqdn: string;
  display_name: string;
  tld: string;
  status: DomainRequestStatus;
  preferred_channel: "email" | "phone" | "whatsapp";
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  message: string | null;
  source: "portal" | "builder";
  organization_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DomainRequestInput {
  fqdn: string;
  organization_id?: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone?: string | null;
  preferred_channel: DomainRequest["preferred_channel"];
  message?: string | null;
  availability?: string | null;
  quoted_register_cents?: number | null;
}

export interface SearchResponse {
  query: string;
  keyword: string;
  currency: string;
  /** This search's reference: sent with the name picked from it, for search-to-purchase reporting. */
  search_id: string | null;
  /** Every name checked, the exact one first (taken and unchecked names included). */
  results: SearchResult[];
  /** The exact name searched, whatever its status. */
  primary: SearchResult | null;
  /** Alternatives that can be registered now, most relevant first, each once (never the primary). */
  recommendations: SearchResult[];
  registrars: { provider: string; status: string; took_ms: number }[];
}

/** Several names checked at once: one result per distinct name, in the order given. */
export interface BulkCheckResponse {
  search_id: string | null;
  currency: string;
  /** Most names one check may hold. */
  limit: number;
  results: SearchResult[];
  /** Entries that aren't domain names, with what to fix. */
  invalid: { entry: string; message: string }[];
  /** Entries skipped because they repeat an earlier name. */
  duplicates: string[];
  registrars: SearchResponse["registrars"];
}

export interface CartItem {
  id: string;
  kind: "register" | "transfer";
  fqdn: string;
  years: number;
  register_cents: number;
  renew_cents: number | null;
  premium: boolean;
  /** What this line costs today, net: first year plus further years at the renewal price. */
  net_cents: number;
  vat_cents: number;
  tax: TaxTreatment;
  added_at: string;
  /** The price is held until then; after it, checkout prices the line again. */
  quote_expires_at: string | null;
  /** A first-year promotion (`register_cents` is the offer): the usual first-year price. */
  standard_register_cents: number | null;
  promotion_label: string | null;
  promotion_ends_at: string | null;
  /** Periods the registry allows for this name, in years. */
  periods: number[];
  /** Extra values the registry wants for this ending, and those already given (by key). */
  required_fields: TldField[];
  tld_fields: Record<string, string>;
  /** The roles besides the registrant this name's registry has (a contact can be chosen for each). */
  contact_roles: OtherRole[];
  /** "none", "required" or "offered": the ending needs a local address in its country (or a trustee). */
  local_presence: "none" | "required" | "offered" | (string & {});
  /** How the customer meets it: "confirmed" (the registrant does) or "trustee" (they buy the trustee service). */
  local_presence_choice: "confirmed" | "trustee" | null;
  /** Staff sell a trustee service with this ending, at this price a year (net of VAT). */
  trustee_enabled: boolean;
  trustee_cents: number | null;
  /** The trustee service's charge for the whole term; it is already included in `net_cents`. */
  trustee_line_cents: number;
}

export interface Cart {
  items: CartItem[];
  currency: string;
  net_cents: number;
  /** The sum of each line's VAT (no VAT on endings that aren't standard-rated). */
  vat_cents: number;
  total_cents: number;
}

/** Contact roles besides the registrant. */
export type OtherRole = "admin" | "tech" | "billing";
export type ContactRole = "registrant" | OtherRole;
export const ROLE_LABELS: Record<ContactRole, string> = {
  registrant: "Registrant",
  admin: "Administrative contact",
  tech: "Technical contact",
  billing: "Billing contact",
};

export interface CheckoutInput {
  contact_id?: string | null;
  contact?: ContactInput | null;
  /** Admin, tech and billing contacts that aren't the registrant: a saved contact or new details. */
  contacts?: Partial<Record<OtherRole, { contact_id?: string | null; contact?: ContactInput | null }>>;
  privacy: boolean;
  auto_renew: boolean;
  payment_method_id?: string | null;
  folder_id?: string | null;
  /** The registry's extra fields per name: `{"example.eu": {"lang_eu": "en"}}`. */
  tld_fields?: Record<string, Record<string, string>>;
  /** The total (VAT included) the customer is looking at; a different one stops with PRICE_CHANGED. */
  expected_total_cents?: number;
  /** Promotion code accepted by the server-side preview and revalidated at checkout. */
  coupon_code?: string | null;
  /** Paid in Apple Pay's or Google Pay's sheet: the approved payment, and whether to keep its card. */
  payment_reference?: string;
  save_card?: boolean;
  make_default?: boolean;
}

export interface CouponPreview {
  coupon_id: string;
  code: string;
  rule_version: number;
  currency: string;
  base_net_cents: number;
  discount_cents: number;
  final_net_cents: number;
  allocations: Record<string, number>;
  minimum_order_cents: number;
  tax_cents: number;
  total_cents: number;
}

/** A 409 PRICE_CHANGED's details: the new total and each line that moved (net). */
export interface PriceChange {
  total_cents: number | null;
  changes: { fqdn: string; old_cents: number | null; new_cents: number }[];
}

export interface ContactInput {
  first_name: string;
  last_name: string;
  organization_name: string | null;
  email: string;
  /** "+44.7700900123" */
  phone: string;
  street: string;
  city: string;
  state: string | null;
  postcode: string;
  country: string;
}

/** Longest value the API accepts for each contact field (backend ContactIn). */
// Registrars keep at most 100 characters of a contact's values (Gransy cuts longer ones silently).
export const CONTACT_MAX = {
  first_name: 100,
  last_name: 100,
  organization_name: 100,
  email: 100,
  phone: 32,
  street: 100,
  city: 100,
  state: 100,
  postcode: 20,
} as const;

export interface Contact extends ContactInput {
  id: string;
  full_name: string;
  address: string;
  verification: "verified" | "pending";
  verification_sent_at: string | null;
  /** The confirmation deadline, while one applies and the e-mail isn't confirmed. */
  verification_due_at: string | null;
  verified_at: string | null;
  /** A domain it owns asks the owner to confirm their e-mail (ICANN's gTLDs, not .uk or .cz). */
  verification_required?: boolean;
  /** `inferred`: the deadline passed without a suspension; the registrar never confirmed it. */
  verification_source?: "registrar" | "inferred" | null;
  /** The domains it owns that ask for the confirmation. */
  verification_domains?: string[];
  label?: string | null;
  archived_at?: string | null;
  /** Sent back as `expected_version` when changing the contact. */
  version?: number;
  /** A change the registries haven't confirmed yet. */
  pending?: { changed_fields: string[]; requested_at: string } | null;
  /** How many domains it is a contact of (contact book listings). */
  domains_count?: number | null;
}

/** A domain a saved contact is a contact of, and in which roles. */
export interface ContactUse {
  domain_id: string;
  fqdn: string;
  roles: ContactRole[];
}

export interface ContactDetail {
  contact: Contact;
  domains: ContactUse[];
  /** Its registrar copy is also another customer's contact: it can't be changed in place. */
  shared: boolean;
  history: { action: string; changed_fields: string[]; at: string }[];
}

export interface ContactImpact {
  changed_fields: (keyof ContactInput)[];
  /** `in_place` needs `confirm_token`; `new_contact_required` is refused (save new details, move the domains). */
  mode: "no_change" | "unused" | "in_place" | "new_contact_required";
  /** Why a new contact is needed: a domain owner's name/organisation/e-mail, or a registrar copy shared with another customer. */
  reason: "owner" | "shared" | null;
  shared: boolean;
  domains: ContactUse[];
  confirm_token: string | null;
}

export type ItemStatus = "queued" | "submitted" | "completed" | "failed" | "refunded" | "cancelled";

export interface OrderItem {
  id: string;
  kind: "register" | "transfer" | "renew" | "restore";
  fqdn: string;
  years: number;
  net_cents: number;
  status: ItemStatus;
  domain_id: string | null;
  /** Why it failed, as the customer is told (a transfer in: the sentence of `failure_kind`). */
  failure_reason: string | null;
  refunded_cents: number | null;
  submitted_at: string | null;
  completed_at: string | null;
  created_at?: string | null;
  /** Transfers in that failed. */
  failure_kind?: TransferFailureKind | null;
  /** Transfers in: when it began waiting for the current registrar, and the latest it then completes. */
  waiting_since?: string | null;
  completes_by?: string | null;
}

export type TransferFailureKind =
  "auth_code" | "locked" | "too_recent" | "refused" | "expired" | "cancelled" | "other";

export interface BulkTransferResult {
  fqdn: string;
  eligible: boolean;
  price_cents: number | null;
  reason: string | null;
}

export interface BulkTransferResponse {
  results: BulkTransferResult[];
  currency: string;
}

/** One transfer of a domain's name, in or away (the Transfers tab). */
export interface TransferRecord {
  direction: "in" | "out";
  /** In: in_progress, completed, rejected, cancelled. Away: pending, approved, rejected, completed, cancelled. */
  status: string;
  started_at: string;
  ended_at: string | null;
  order_id: string | null;
  reason: string | null;
  auto_completes_at: string | null;
  answered_via: "email" | "portal" | null;
}

/** What the registrant's e-mailed transfer-away link shows (no sign-in). */
export interface TransferOutLink {
  fqdn: string;
  status: string;
  detected_at: string;
  auto_completes_at: string;
}

export interface Order {
  id: string;
  number: string;
  status:
    | "pending_payment"
    | "payment_failed"
    | "paid"
    | "processing"
    | "completed"
    | "partially_failed"
    | "failed"
    | "cancelled";
  currency: string;
  net_cents: number;
  vat_cents: number;
  total_cents: number;
  invoice_id: string | null;
  payment_method_label: string | null;
  created_at: string;
  completed_at: string | null;
  items: OrderItem[];
  payment_status: "unpaid" | "pending" | "paid" | "failed";
  fulfilment_status: "pending" | "processing" | "completed" | "partially_failed" | "failed" | "cancelled";
  cancellation_status: "not_requested" | "requested" | "confirmed" | "rejected" | "uncertain";
  refund_status: "not_applicable" | "pending" | "partial" | "completed" | "failed";
  history: OrderHistory[];
}

export interface OrderHistory {
  id: string;
  event: string;
  from_status: string | null;
  to_status: string | null;
  detail: Record<string, unknown>;
  created_at: string;
  actor_user_id: string | null;
}

export interface SiteRef {
  id: string;
  name: string;
  hostname: string;
  kind: string;
  /** Scheme for links to the site and its domains (http while site certificates are unavailable). */
  link_scheme: LinkScheme;
}

export interface Domain {
  id: string;
  fqdn: string;
  display_name: string;
  tld: string;
  status: DomainStatus;
  health: DomainHealth;
  origin: "registered" | "transferred";
  folder_id: string | null;
  site_id: string | null;
  site: SiteRef | null;
  connection: "none" | "configuring" | "connected" | "error";
  connection_error: string | null;
  is_primary: boolean;
  registered_at: string | null;
  expires_at: string | null;
  days_left: number | null;
  recoverable_until: string | null;
  deleted_at: string | null;
  auto_renew: boolean;
  transfer_lock: boolean;
  privacy: boolean;
  dnssec: "off" | "enabling" | "on" | "disabling";
  nameserver_mode: "cortexgrip" | "custom";
  nameservers: string[];
  renewal_price_cents: number | null;
  restore_price_cents: number | null;
  renewal_failed_at: string | null;
  renewal_failure: string | null;
  renewal_attempts: number;
  /** True once automatic retries have run out: the customer has to renew by hand. */
  renewal_retries_stopped: boolean;
  /** A higher renewal price (net, cents) waiting for the customer to accept it: automatic renewal is paused. */
  renewal_price_pending_cents: number | null;
  /** Why the DNS zone could not be created after registration (the domain is still theirs); null when fine. */
  zone_failure: string | null;
  zone_confirmed_at: string | null;
  transfer_window_open: boolean;
  /** CortexGrip support suspended it: every change is refused (`DOMAIN_SUSPENDED`) until they lift it. */
  suspended?: boolean;
  created_at: string;
}

export interface Folder {
  id: string;
  name: string;
  domain_count: number;
  created_at: string;
}

export interface ExternalDomain {
  id: string;
  hostname: string;
  status: "pending_verification" | "active" | "failed" | "unknown";
  site: SiteRef;
  created_at: string | null;
}

export interface DomainList {
  domains: Domain[];
  total: number;
  page: number;
  page_size: number;
  all_count: number;
  unfiled_count: number;
  folders: Folder[];
  external: ExternalDomain[];
  currency: string;
}

export type OperationKind =
  "dnssec_on" | "dnssec_off" | "nameservers" | "transfer_lock" | "privacy" | "transfer_out" | "contacts";

/** A `contacts` operation's detail: the roles moving, and the approvals a change of owner waits for. */
export interface ContactChangeDetail {
  roles: Partial<Record<ContactRole, string>>;
  previous: Partial<Record<ContactRole, string>>;
  owner_change: boolean;
  awaiting_approval: boolean;
  authorizations: {
    title: string;
    status: "new" | "approved" | "rejected" | (string & {});
    emails: string[];
  }[];
}

export type RoleChoice = { contact_id: string } | { contact: ContactInput };

export interface ContactMove {
  changes: {
    role: ContactRole;
    current: Contact | null;
    new: Contact | null;
    new_details: Record<string, string | null> | null;
  }[];
  /** The registrant changes: the registrar e-mails both approvers and makes it only once both approve. */
  owner_change: boolean;
  approvers: string[];
}

export interface Operation {
  id: string;
  kind: OperationKind;
  status: "pending" | "completed" | "failed" | "cancelled";
  detail: Record<string, unknown>;
  failure: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface DomainDetail {
  domain: Domain;
  contact: Contact | null;
  /** Each contact role's contact; a role whose registry has none is the registrant. */
  contacts?: Partial<Record<ContactRole, Contact>>;
  /** The roles besides the registrant its registry has. */
  contact_roles?: OtherRole[];
  record_count: number;
  /** Mail is delivered through this domain's CortexGrip zone: moving its nameservers away would stop it. */
  email_in_use?: boolean;
  operations: Operation[];
  capabilities: Capability[];
  folder: Folder | null;
  open_order_id: string | null;
  default_nameservers: string[];
  currency: string;
}

export interface DnsRecord {
  id: string;
  name: string;
  type: string;
  content: string;
  ttl: number;
  priority: number | null;
  status: "pending" | "live" | "error";
  managed_by: string | null;
  error: string | null;
  created_by_email: string | null;
  created_at: string;
  updated_at: string;
  confirmed_at: string | null;
}

export interface DnsConflict {
  kind: "cname_with_other" | "duplicate_cname" | "duplicate";
  name: string;
  record_ids: string[];
  keep_id: string | null;
  message: string;
}

export interface ConnectedRow {
  key: "website" | "email" | "www";
  title: string;
  detail: string;
  status: "connected" | "pending" | "needs_fix" | "active" | "none";
}

export interface Zone {
  managed: boolean;
  nameserver_mode: string;
  records: DnsRecord[];
  conflicts: DnsConflict[];
  connected: ConnectedRow[];
  zone_confirmed_at: string | null;
  zone_version: number;
  /** Why the zone could not be created after registration, until a retry works. */
  zone_failure?: string | null;
}

export interface DnsChange {
  op: "add" | "update" | "delete";
  record_id?: string | null;
  name?: string;
  type?: string;
  content?: string;
  ttl?: number;
  priority?: number | null;
}

export interface DnsPreview {
  rows: { change: "kept" | "new" | "removed" | "changed"; record: DnsRecord; previous: DnsRecord | null }[];
  conflicts: DnsConflict[];
  impacts: { area: "website" | "email"; affected: boolean; message: string }[];
  warnings: string[];
}

export interface ConnectionStep {
  key: "selected" | "dns" | "verify" | "primary";
  state: "completed" | "in_progress" | "not_yet" | "failed";
}

export interface Connection {
  domain: Domain;
  site: SiteRef | null;
  steps: ConnectionStep[];
  detail: string | null;
}

export interface DomainEvent {
  id: string;
  category: string;
  summary: string;
  actor_email: string | null;
  created_at: string;
}

export interface Diagnosis {
  hostname: string;
  registrar: string | null;
  records: { type: string; host: string; value: string; found: string[]; matches: boolean | null }[];
}

export interface TransferCheck {
  valid: boolean;
  expires_at: string | null;
  current_registrar: string | null;
  price_cents: number | null;
  currency: string;
}

export interface ListFilters {
  q?: string;
  status?: string;
  expires?: string;
  folder?: string;
  auto_renew?: string;
  connected?: string;
  sort?: string;
  page?: number;
  page_size?: number;
}

// ── API ─────────────────────────────────────────────────────────────────────
const enc = encodeURIComponent;
const base = (orgId: string) => `/v1/organizations/${enc(orgId)}/domains`;

/**
 * A fresh key for one attempt to pay (`Idempotency-Key`): sending the same checkout again with it never buys
 * twice, however many times the browser retries it.
 */
export function newCheckoutKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/** Answers that say a request clashed with another, not that the payment was refused: it may have gone through. */
const NOT_A_REFUSAL = new Set([
  "CHECKOUT_IN_PROGRESS",
  "CHARGE_IN_PROGRESS", // a renewal, recovery or upgrade still being made: ask again with the same key
  "CONCURRENT_MODIFICATION",
  "INVOICE_LOCKED",
]);

/**
 * Whether the server's answer ends the attempt, so the next try gets a new key. A definite refusal does
 * (nothing was charged); no answer at all, a server error, or "still processing" does not: the payment may have
 * gone through, and asking again with the same key returns its order instead of paying twice.
 */
export function attemptEnded(err: unknown): boolean {
  return err instanceof ApiError && err.status >= 400 && err.status < 500 && !NOT_A_REFUSAL.has(err.reason);
}

export const domainsApi = {
  search: (orgId: string, q: string) =>
    authedPortal<SearchResponse>(`${base(orgId)}/search`, { query: { q } }),
  bulkCheck: (orgId: string, names: string[]) =>
    authedPortal<BulkCheckResponse>(`${base(orgId)}/check`, { method: "POST", body: { names } }),

  requests: () => authedPortal<{ requests: DomainRequest[] }>("/v1/domains/requests"),
  createRequest: (body: DomainRequestInput) =>
    authedPortal<DomainRequest>("/v1/domains/requests", {
      method: "POST",
      body: { ...body, source: "portal" },
    }),
  cancelRequest: (id: string) =>
    authedPortal<DomainRequest>(`/v1/domains/requests/${enc(id)}/cancel`, { method: "POST" }),

  cart: (orgId: string) => authedPortal<Cart>(`${base(orgId)}/cart`),
  addToCart: (
    orgId: string,
    body: {
      fqdn: string;
      kind?: "register" | "transfer";
      years?: number;
      auth_code?: string;
      search_id?: string | null;
    },
  ) => authedPortal<CartItem>(`${base(orgId)}/cart`, { method: "POST", body }),
  setCartYears: (orgId: string, itemId: string, years: number) =>
    authedPortal<Cart>(`${base(orgId)}/cart/${enc(itemId)}`, { method: "PATCH", body: { years } }),
  /** Replaces the registry's extra fields for a cart line (send every value, not just the changed one). */
  setCartFields: (orgId: string, itemId: string, tld_fields: Record<string, string>) =>
    authedPortal<Cart>(`${base(orgId)}/cart/${enc(itemId)}`, { method: "PATCH", body: { tld_fields } }),
  /** Attaches a file (PDF, JPG or PNG, up to 5 MB) to a "file" detail; the returned id is the detail's value. */
  uploadCheckoutFile: (orgId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return authedPortal<{ id: string; name: string; content_type: string; size_bytes: number }>(
      `${base(orgId)}/files`,
      { method: "POST", body: form },
    );
  },
  /** How the customer meets a local-presence ending: confirm it, buy the trustee service, or clear the choice. */
  setLocalPresence: (orgId: string, itemId: string, local_presence: "confirmed" | "trustee" | "none") =>
    authedPortal<Cart>(`${base(orgId)}/cart/${enc(itemId)}`, { method: "PATCH", body: { local_presence } }),
  /** What was last entered with a saved contact for each ending's extra fields (to fill in again). */
  contactTldFields: (orgId: string, contactId: string, tlds: string[]) =>
    authedPortal<{ values: Record<string, Record<string, string>> }>(
      `${base(orgId)}/contacts/${enc(contactId)}/tld-fields?${tlds.map((t) => `tld=${enc(t)}`).join("&")}`,
    ),
  tldRules: (tlds: string[]) =>
    authedPortal<{ tlds: TldRules[] }>(
      `/v1/domains/tld-rules?${tlds.map((t) => `tld=${enc(t.replace(/^\./, ""))}`).join("&")}`,
    ),
  removeFromCart: (orgId: string, itemId: string) =>
    authedPortal<Cart>(`${base(orgId)}/cart/${enc(itemId)}`, { method: "DELETE" }),
  clearCart: (orgId: string) => authedPortal<Cart>(`${base(orgId)}/cart`, { method: "DELETE" }),
  checkTransfer: (orgId: string, fqdn: string, auth_code: string) =>
    authedPortal<TransferCheck>(`${base(orgId)}/transfers/check`, {
      method: "POST",
      body: { fqdn, auth_code },
    }),
  /** `idempotencyKey`: one per attempt (`newCheckoutKey`); sending it again returns the order it made. */
  checkout: (orgId: string, body: CheckoutInput, idempotencyKey: string) =>
    authedPortal<Order>(`${base(orgId)}/checkout`, {
      method: "POST",
      body,
      headers: { "Idempotency-Key": idempotencyKey },
    }),
  /** Start paying for the cart in a wallet's sheet (only authorised there; taken by `checkout`). */
  startWalletCheckout: (orgId: string, coupon_code?: string | null) =>
    authedPortal<{ payment_reference: string; client_secret: string | null }>(
      `${base(orgId)}/checkout/wallet`,
      {
        method: "POST",
        body: coupon_code ? { coupon_code } : {},
      },
    ),
  previewCoupon: (orgId: string, coupon_code: string) =>
    authedPortal<CouponPreview>(`${base(orgId)}/coupon/preview`, {
      method: "POST",
      body: { coupon_code },
    }),
  orders: (orgId: string) =>
    authedPortal<{ orders: Order[]; total: number; page: number; page_size: number }>(
      `${base(orgId)}/orders`,
    ),
  order: (orgId: string, orderId: string) => authedPortal<Order>(`${base(orgId)}/orders/${enc(orderId)}`),
  cancelOrder: (orgId: string, orderId: string) =>
    authedPortal<Order>(`${base(orgId)}/orders/${enc(orderId)}/cancel`, {
      method: "POST",
      body: { confirm: true },
    }),

  contacts: (orgId: string) => authedPortal<{ contacts: Contact[] }>(`${base(orgId)}/contacts`),

  // ── contact book ───────────────────────────────────────────────────────
  contactBook: (orgId: string, includeArchived = false) =>
    authedPortal<{ contacts: Contact[] }>(`${base(orgId)}/contacts`, {
      query: includeArchived ? { include_archived: "true" } : undefined,
    }),
  /** Saves the details; the same details saved before answer with that contact (`created: false`). */
  createContact: (orgId: string, body: ContactInput & { label?: string | null }) =>
    authedPortal<Contact>(`${base(orgId)}/contacts`, { method: "POST", body }),
  contactDetail: (orgId: string, contactId: string) =>
    authedPortal<ContactDetail>(`${base(orgId)}/contacts/${enc(contactId)}`),
  /** What changing a contact to `body` would do (nothing is changed). */
  contactImpact: (orgId: string, contactId: string, body: ContactInput & { label?: string | null }) =>
    authedPortal<ContactImpact>(`${base(orgId)}/contacts/${enc(contactId)}/impact`, { method: "POST", body }),
  changeContact: (
    orgId: string,
    contactId: string,
    body: ContactInput & { label?: string | null; expected_version: number; confirm_token?: string | null },
  ) => authedPortal<Contact>(`${base(orgId)}/contacts/${enc(contactId)}`, { method: "PUT", body }),
  archiveContact: (orgId: string, contactId: string) =>
    authedPortal<Contact>(`${base(orgId)}/contacts/${enc(contactId)}/archive`, { method: "POST" }),
  unarchiveContact: (orgId: string, contactId: string) =>
    authedPortal<Contact>(`${base(orgId)}/contacts/${enc(contactId)}/unarchive`, { method: "POST" }),

  list: (orgId: string, filters: ListFilters) =>
    authedPortal<DomainList>(base(orgId), { query: filters as Record<string, string | number | undefined> }),
  detail: (orgId: string, domainId: string) => authedPortal<DomainDetail>(`${base(orgId)}/${enc(domainId)}`),
  update: (
    orgId: string,
    domainId: string,
    body: { auto_renew?: boolean; folder_id?: string | null; unfile?: boolean },
  ) => authedPortal<Domain>(`${base(orgId)}/${enc(domainId)}`, { method: "PATCH", body }),
  remove: (orgId: string, domainId: string) =>
    authedPortal<void>(`${base(orgId)}/${enc(domainId)}`, { method: "DELETE" }),
  events: (orgId: string, domainId: string, category?: "security" | "nameservers" | "dns") =>
    authedPortal<{ events: DomainEvent[] }>(`${base(orgId)}/${enc(domainId)}/events`, {
      query: { category },
    }),

  /** `idempotencyKey`: one per attempt (`attemptKey`); sending it again returns the order it made. */
  renew: (
    orgId: string,
    domainId: string,
    years: number,
    payment_method_id?: string | null,
    idempotencyKey?: string,
    /** The yearly price (net, cents) the customer was shown: if it has changed, nothing is charged. */
    expectedPriceCents?: number | null,
  ) =>
    authedPortal<Order>(`${base(orgId)}/${enc(domainId)}/renew`, {
      method: "POST",
      body: {
        years,
        payment_method_id,
        ...(expectedPriceCents ? { expected_price_cents: expectedPriceCents } : {}),
      },
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
    }),
  restore: (orgId: string, domainId: string, payment_method_id?: string | null, idempotencyKey?: string) =>
    authedPortal<Order>(`${base(orgId)}/${enc(domainId)}/restore`, {
      method: "POST",
      body: { payment_method_id },
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
    }),
  setTransferLock: (orgId: string, domainId: string, locked: boolean) =>
    authedPortal<Operation>(`${base(orgId)}/${enc(domainId)}/transfer-lock`, {
      method: "PUT",
      body: { locked },
    }),
  setPrivacy: (orgId: string, domainId: string, enabled: boolean) =>
    authedPortal<Operation>(`${base(orgId)}/${enc(domainId)}/privacy`, { method: "PUT", body: { enabled } }),
  setDnssec: (orgId: string, domainId: string, enabled: boolean) =>
    authedPortal<Operation>(`${base(orgId)}/${enc(domainId)}/dnssec`, { method: "PUT", body: { enabled } }),
  setNameservers: (
    orgId: string,
    domainId: string,
    mode: "cortexgrip" | "custom",
    nameservers: string[] = [],
  ) =>
    authedPortal<Operation>(`${base(orgId)}/${enc(domainId)}/nameservers`, {
      method: "PUT",
      body: { mode, nameservers },
    }),
  operation: (orgId: string, domainId: string, operationId: string) =>
    authedPortal<Operation>(`${base(orgId)}/${enc(domainId)}/operations/${enc(operationId)}`),
  authCode: (orgId: string, domainId: string, delivery: "email" | "show") =>
    authedPortal<{ code: string | null; sent_to: string | null }>(
      `${base(orgId)}/${enc(domainId)}/auth-code`,
      {
        method: "POST",
        body: { delivery },
      },
    ),
  bulkTransfers: (orgId: string, items: { fqdn: string; auth_code: string }[], addToCart = false) =>
    authedPortal<BulkTransferResponse>(`${base(orgId)}/transfers/bulk`, {
      method: "POST",
      body: { items, add_to_cart: addToCart },
    }),
  transfers: (orgId: string, domainId: string) =>
    authedPortal<{ transfers: TransferRecord[] }>(`${base(orgId)}/${enc(domainId)}/transfers`),
  /** The registrant's e-mailed link: no sign-in, the token is the credential. */
  transferOutLink: (token: string) =>
    request<TransferOutLink>(config.apiUrl, `/v1/transfer-out/${enc(token)}`),
  answerTransferOutLink: (token: string, approve: boolean) =>
    request<TransferOutLink>(config.apiUrl, `/v1/transfer-out/${enc(token)}`, {
      method: "POST",
      body: { approve },
    }),
  answerTransferOut: (orgId: string, domainId: string, approve: boolean) =>
    authedPortal<Operation>(`${base(orgId)}/${enc(domainId)}/transfer-out`, {
      method: "POST",
      body: { approve },
    }),

  updateContact: (orgId: string, domainId: string, body: ContactInput) =>
    authedPortal<Contact>(`${base(orgId)}/${enc(domainId)}/contact`, { method: "PUT", body }),
  previewContacts: (orgId: string, domainId: string, contacts: Partial<Record<ContactRole, RoleChoice>>) =>
    authedPortal<ContactMove>(`${base(orgId)}/${enc(domainId)}/contacts/preview`, {
      method: "POST",
      body: { contacts },
    }),
  /** Move the domain's roles to other contacts; a new registrant waits for both owners' approval. */
  moveContacts: (
    orgId: string,
    domainId: string,
    contacts: Partial<Record<ContactRole, RoleChoice>>,
    confirmOwnerChange = false,
  ) =>
    authedPortal<Operation>(`${base(orgId)}/${enc(domainId)}/contacts`, {
      method: "PUT",
      body: { contacts, confirm_owner_change: confirmOwnerChange },
    }),
  cancelContactChange: (orgId: string, domainId: string, operationId: string) =>
    authedPortal<Operation>(`${base(orgId)}/${enc(domainId)}/operations/${enc(operationId)}/cancel`, {
      method: "POST",
    }),
  resendVerification: (orgId: string, domainId: string) =>
    authedPortal<Contact>(`${base(orgId)}/${enc(domainId)}/contact/resend-verification`, { method: "POST" }),

  zone: (orgId: string, domainId: string) => authedPortal<Zone>(`${base(orgId)}/${enc(domainId)}/dns`),
  previewDns: (orgId: string, domainId: string, changes: DnsChange[]) =>
    authedPortal<DnsPreview>(`${base(orgId)}/${enc(domainId)}/dns/preview`, {
      method: "POST",
      body: { changes },
    }),
  applyDns: (
    orgId: string,
    domainId: string,
    changes: DnsChange[],
    expectedZoneVersion: number,
    idempotencyKey: string,
  ) =>
    authedPortal<Zone>(`${base(orgId)}/${enc(domainId)}/dns/changes`, {
      method: "POST",
      body: { changes, expected_zone_version: expectedZoneVersion },
      headers: { "Idempotency-Key": idempotencyKey },
    }),
  recheckDns: (orgId: string, domainId: string) =>
    authedPortal<Zone>(`${base(orgId)}/${enc(domainId)}/dns/recheck`, { method: "POST" }),

  connection: (orgId: string, domainId: string) =>
    authedPortal<Connection>(`${base(orgId)}/${enc(domainId)}/connection`),
  connect: (orgId: string, domainId: string, site_id: string, primary: boolean) =>
    authedPortal<Connection>(`${base(orgId)}/${enc(domainId)}/connection`, {
      method: "PUT",
      body: { site_id, primary },
    }),
  disconnect: (orgId: string, domainId: string) =>
    authedPortal<Domain>(`${base(orgId)}/${enc(domainId)}/connection`, { method: "DELETE" }),
  makePrimary: (orgId: string, domainId: string) =>
    authedPortal<Domain>(`${base(orgId)}/${enc(domainId)}/primary`, { method: "POST" }),

  folders: (orgId: string) => authedPortal<{ folders: Folder[] }>(`${base(orgId)}/folders`),
  createFolder: (orgId: string, name: string) =>
    authedPortal<Folder>(`${base(orgId)}/folders`, { method: "POST", body: { name } }),
  renameFolder: (orgId: string, folderId: string, name: string) =>
    authedPortal<void>(`${base(orgId)}/folders/${enc(folderId)}`, { method: "PATCH", body: { name } }),
  deleteFolder: (orgId: string, folderId: string) =>
    authedPortal<void>(`${base(orgId)}/folders/${enc(folderId)}`, { method: "DELETE" }),
  move: (orgId: string, domain_ids: string[], folder_id: string | null) =>
    authedPortal<void>(`${base(orgId)}/move`, { method: "POST", body: { domain_ids, folder_id } }),

  diagnose: (orgId: string, hostname: string, site_id?: string | null) =>
    authedPortal<Diagnosis>(`${base(orgId)}/diagnose`, { query: { hostname, site_id } }),
};

// ── hooks ───────────────────────────────────────────────────────────────────
export const domainKeys = {
  all: (orgId: string) => ["domains", orgId] as const,
  list: (orgId: string, filters: ListFilters) => ["domains", orgId, "list", filters] as const,
  detail: (orgId: string, id: string) => ["domains", orgId, "detail", id] as const,
  zone: (orgId: string, id: string) => ["domains", orgId, "zone", id] as const,
  cart: (orgId: string) => ["domains", orgId, "cart"] as const,
  requests: () => ["domain-requests"] as const,
  order: (orgId: string, id: string) => ["domains", orgId, "order", id] as const,
  transfers: (orgId: string, id: string) => ["domains", orgId, "transfers", id] as const,
};

export function useDomainDetail(domainId: string | undefined, opts: { poll?: boolean } = {}) {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: domainKeys.detail(orgId, domainId ?? ""),
    queryFn: () => domainsApi.detail(orgId, domainId!),
    enabled: Boolean(domainId),
    refetchInterval: opts.poll ? 4000 : false,
  });
}

export function useCart() {
  const { orgId } = useOrganization();
  return useQuery({ queryKey: domainKeys.cart(orgId), queryFn: () => domainsApi.cart(orgId) });
}

// ── wording ─────────────────────────────────────────────────────────────────
export type PillTone = "success" | "danger" | "warning" | "neutral" | "info";

/** The Figma status pill for a domain: label + tone. */
export function healthPill(health: DomainHealth): { label: string; tone: PillTone } {
  switch (health) {
    case "active":
      return { label: "Active", tone: "success" };
    case "renewal_failed":
      return { label: "Renewal failed", tone: "danger" };
    case "expiring_soon":
      return { label: "Expiring soon", tone: "warning" };
    case "expired":
      return { label: "Expired", tone: "danger" };
    case "restoring":
      return { label: "Recovery processing", tone: "warning" };
    case "unrecoverable":
      return { label: "No longer recoverable", tone: "danger" };
    case "registering":
      return { label: "Registration processing", tone: "warning" };
    case "transferring_in":
      return { label: "Transfer in progress", tone: "warning" };
    case "transferring_out":
      return { label: "Transfer pending", tone: "warning" };
    case "transferred_out":
      return { label: "Transfer complete", tone: "success" };
    case "failed":
      return { label: "Registration failed", tone: "danger" };
    default:
      return { label: "Removed", tone: "neutral" };
  }
}

/** "in 3 days" / "today" / "expired" / "4 days ago" */
export function relativeDays(days: number | null): string {
  if (days === null) return "";
  if (days === 0) return "today";
  if (days < 0) return days === -1 ? "yesterday" : `${-days} days ago`;
  return days === 1 ? "tomorrow" : `in ${days} days`;
}

/** Calendar days from today to the day `iso` falls on, both read in the display time zone. */
export function daysUntil(iso: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  return civilDay(iso) - civilDay(now);
}

/** "26 Sep 2026" */
export const longDate = formatDayMonthYear;

/** Whether this domain's registry asks its owner to confirm their e-mail (ICANN's gTLDs, not .uk or .cz). */
export const verificationApplies = (contact: Contact | null | undefined, fqdn: string): boolean =>
  Boolean(contact?.verification_domains?.includes(fqdn));

/** A contact's e-mail verification as a badge, with the longer explanation where there is one. */
export function verificationBadge(contact: Contact): {
  label: string;
  tone: "success" | "warning" | "neutral";
  detail?: string;
} {
  if (!contact.verification_required) return { label: "Verification not required", tone: "neutral" };
  if (contact.verification === "verified")
    return contact.verification_source === "inferred"
      ? {
          label: "Not suspended by the deadline",
          tone: "success",
          detail: "No suspension by the deadline (not confirmed by the registrar)",
        }
      : { label: "Email verified", tone: "success" };
  return {
    label: contact.verification_due_at
      ? `Confirm email by ${longDate(contact.verification_due_at)}`
      : "Email not verified",
    tone: "warning",
  };
}

/** "SB" for saltandbloom.com — the heading tile. */
export function domainInitials(fqdn: string): string {
  const sld = fqdn.split(".")[0] ?? fqdn;
  const parts = sld.split(/[-_]/).filter(Boolean);
  if (parts.length > 1) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return sld.slice(0, 2).toUpperCase();
}

export function has(detail: DomainDetail | undefined, capability: Capability): boolean {
  return Boolean(detail?.capabilities.includes(capability));
}

/** Price of a cart line for a number of years (first year at the registration price). */
export function linePrice(registerCents: number, renewCents: number | null, years: number): number {
  return registerCents + (renewCents ?? registerCents) * (years - 1);
}

/** The terms a name can be registered for (1–10 when the registry didn't say), always including `current`. */
export function termOptions(periods: number[] | undefined, current?: number): number[] {
  const base = periods && periods.length > 0 ? periods : Array.from({ length: 10 }, (_, i) => i + 1);
  return current === undefined || base.includes(current) ? base : [...base, current].sort((a, b) => a - b);
}

/** A real first-year offer: the usual price is known and higher than what's charged. */
export function hasPromotion(p: {
  register_cents: number | null;
  standard_register_cents: number | null;
}): boolean {
  return (
    p.standard_register_cents !== null &&
    p.register_cents !== null &&
    p.standard_register_cents > p.register_cents
  );
}

/** "Spring offer until 31 Oct 2026" — the promotion pill. */
export function promotionText(p: {
  promotion_label: string | null;
  promotion_ends_at: string | null;
}): string {
  const label = p.promotion_label?.trim() || "Offer";
  return p.promotion_ends_at ? `${label} until ${longDate(p.promotion_ends_at)}` : label;
}

/** Why an ending carries no VAT, for the order summary ("zero-rated"); null when VAT is added. */
export function noVatReason(tax: TaxTreatment): string | null {
  switch (tax) {
    case "zero_rated":
      return "zero-rated";
    case "exempt":
      return "exempt from VAT";
    case "outside_scope":
      return "outside the scope of VAT";
    default:
      return null;
  }
}

/** ".co.uk" for "shop.co.uk". */
export function endingOf(fqdn: string): string {
  const dot = fqdn.indexOf(".");
  return dot >= 0 ? fqdn.slice(dot) : fqdn;
}

/** "+44.7700900123" ⇄ dial code + number, for the phone field. */
export function splitPhone(phone: string | null | undefined): { dial: string; number: string } {
  const m = /^\+(\d{1,3})\.(\d+)$/.exec(phone ?? "");
  return m ? { dial: m[1]!, number: m[2]! } : { dial: "44", number: (phone ?? "").replace(/\D/g, "") };
}

export function joinPhone(dial: string, number: string): string {
  return `+${dial.replace(/\D/g, "")}.${number.replace(/\D/g, "").replace(/^0+/, "")}`;
}

export const DNS_TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "SRV", "CAA"] as const;
